import { useEffect, useState, useMemo } from 'react';
import { Languages, Plus, Trash2, ChevronDown, AlertCircle, Info, ExternalLink, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface HreflangEntry {
  lang: string;
  href: string;
}

export interface HreflangCluster {
  id: string;
  entries: HreflangEntry[];
}

interface HreflangManagerProps {
  domain: string;
  clusters: HreflangCluster[];
  onChange: (clusters: HreflangCluster[]) => void;
  /** URLs currently uploaded, used as input for the auto-detect button. */
  knownUrls?: string[];
}

const STORAGE_PREFIX = 'sh.hreflang.';

// Authoritative ISO 639-1 set. Used to validate the language part of a
// hreflang code AND to gate auto-detect from treating any old 2-letter path
// segment as a locale. Without this allowlist, /qa/, /us/, /eu/, /cn/,
// /uk/ (Ukrainian by ISO but customers always mean United Kingdom),
// /wp/, /js/, etc. all matched the previous `[a-z]{2}` regex and would
// silently end up as hreflang clusters — exporting staging URLs as
// production alternates to Google.
const ISO_639_1: ReadonlySet<string> = new Set([
  'aa','ab','ae','af','ak','am','an','ar','as','av','ay','az',
  'ba','be','bg','bh','bi','bm','bn','bo','br','bs',
  'ca','ce','ch','co','cr','cs','cu','cv','cy',
  'da','de','dv','dz',
  'ee','el','en','eo','es','et','eu',
  'fa','ff','fi','fj','fo','fr','fy',
  'ga','gd','gl','gn','gu','gv',
  'ha','he','hi','ho','hr','ht','hu','hy','hz',
  'ia','id','ie','ig','ii','ik','io','is','it','iu',
  'ja','jv',
  'ka','kg','ki','kj','kk','kl','km','kn','ko','kr','ks','ku','kv','kw','ky',
  'la','lb','lg','li','ln','lo','lt','lu','lv',
  'mg','mh','mi','mk','ml','mn','mr','ms','mt','my',
  'na','nb','nd','ne','ng','nl','nn','no','nr','nv','ny',
  'oc','oj','om','or','os',
  'pa','pi','pl','ps','pt',
  'qu',
  'rm','rn','ro','ru','rw',
  'sa','sc','sd','se','sg','si','sk','sl','sm','sn','so','sq','sr','ss','st','su','sv','sw',
  'ta','te','tg','th','ti','tk','tl','tn','to','tr','ts','tt','tw','ty',
  'ug','uk','ur','uz',
  've','vi','vo',
  'wa','wo',
  'xh',
  'yi','yo',
  'za','zh','zu',
]);

// Region part is ISO 3166-1 alpha-2. We accept any [a-z]{2} for the region
// half since the practical "wrong region for this language" case is much
// rarer than the "wrong language code" case and Google tolerates region
// codes outside the official set. Validation happens lang-first.
const LANG_REGION_RE = /^(x-default|[a-z]{2}(-[a-z]{2})?)$/i;

function isValidLangCode(code: string): boolean {
  if (!code) return false;
  const lower = code.toLowerCase();
  if (lower === 'x-default') return true;
  if (!LANG_REGION_RE.test(lower)) return false;
  const langPart = lower.split('-')[0];
  return ISO_639_1.has(langPart);
}

function isValidUrl(href: string): boolean {
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Read previously-saved clusters for this domain out of localStorage.
 * Falls back to empty array on any error (corrupt JSON, schema drift).
 */
export function loadHreflangClusters(domain: string): HreflangCluster[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + domain);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c: unknown): c is HreflangCluster => {
        const cluster = c as HreflangCluster;
        // Filter zero/one-entry clusters that would render as ghost rows
        // in the editor and be silently dropped by buildHreflangIndex
        // anyway. A real cluster has at least 2 entries.
        return !!cluster && Array.isArray(cluster.entries) && cluster.entries.length >= 2;
      })
      .map((c) => ({ id: c.id || crypto.randomUUID(), entries: c.entries }));
  } catch {
    return [];
  }
}

/**
 * Build a `loc → HreflangEntry[]` index from the saved clusters so the
 * publish step can decorate every matching URL in one O(1) lookup. Cluster
 * membership is the alternate list: a URL in a 3-language cluster gets all
 * 3 entries (including its own self-reference per Google's spec).
 */
export function buildHreflangIndex(
  clusters: HreflangCluster[]
): Map<string, HreflangEntry[]> {
  const idx = new Map<string, HreflangEntry[]>();
  for (const cluster of clusters) {
    const valid = cluster.entries.filter(
      (e) => isValidLangCode(e.lang) && isValidUrl(e.href)
    );
    if (valid.length < 2) continue; // a single-URL "cluster" carries no alternate info
    for (const entry of valid) {
      idx.set(entry.href, valid);
    }
  }
  return idx;
}

/**
 * Auto-detect hreflang clusters by spotting locale prefixes in URL paths.
 *
 * Heuristic (covers ~80% of real-world i18n sites):
 *   1. Parse every URL, split the pathname into segments.
 *   2. If the first segment looks like a locale code (`en`, `en-us`,
 *      `x-default`), strip it and treat the remainder as the cluster key.
 *   3. Group URLs by (host, stripped-path). Any group with ≥2 distinct
 *      locales becomes a cluster.
 *
 * Doesn't try to cover subdomain (`es.example.com`) or ccTLD
 * (`example.es`) patterns yet — those need cross-domain inference the
 * user should confirm by hand. Returns clusters in the same shape the
 * editor manages.
 */
export function detectHreflangClusters(urls: string[]): HreflangCluster[] {
  // key = `${host}|${strippedPath}`, value = list of {lang, href}.
  const groups = new Map<string, HreflangEntry[]>();

  for (const raw of urls) {
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      continue;
    }
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length === 0) continue;
    const first = segments[0].toLowerCase();
    // Allowlist-gated. Plain regex would clobber /qa/, /us/, /eu/, /cn/,
    // /uk/, /wp/, /js/ etc. and silently ship staging URLs as Google
    // hreflang alternates.
    if (!isValidLangCode(first)) continue;

    const strippedPath = '/' + segments.slice(1).join('/') + (parsed.search || '');
    const key = `${parsed.host}|${strippedPath}`;
    const list = groups.get(key) || [];
    // Avoid duplicate lang in the same cluster (would have come up if the
    // upload contained the same URL twice).
    if (list.some((e) => e.lang.toLowerCase() === first)) continue;
    list.push({ lang: first, href: raw });
    groups.set(key, list);
  }

  const clusters: HreflangCluster[] = [];
  for (const entries of groups.values()) {
    if (entries.length < 2) continue;
    clusters.push({ id: crypto.randomUUID(), entries });
  }
  return clusters;
}

function emptyCluster(): HreflangCluster {
  return {
    id: crypto.randomUUID(),
    entries: [
      { lang: '', href: '' },
      { lang: '', href: '' },
    ],
  };
}

export function HreflangManager({
  domain,
  clusters,
  onChange,
  knownUrls = [],
}: HreflangManagerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detectNotice, setDetectNotice] = useState<string | null>(null);

  // Preview of what auto-detect would find without applying it. Recalculated
  // whenever the uploaded URL list changes so the button label can show the
  // count up-front and decay gracefully when the URL list doesn't contain
  // locale-prefixed paths.
  const detectableClusters = useMemo(
    () => detectHreflangClusters(knownUrls),
    [knownUrls]
  );

  const handleAutoDetect = () => {
    if (detectableClusters.length === 0) {
      setDetectNotice(
        'No locale-prefixed URLs detected. Auto-detect spots /en/, /es-MX/, /x-default/ style paths in the first segment.'
      );
      return;
    }
    if (clusters.length > 0) {
      const ok = confirm(
        `Replace ${clusters.length} existing cluster${clusters.length === 1 ? '' : 's'} with ${detectableClusters.length} auto-detected cluster${detectableClusters.length === 1 ? '' : 's'}?`
      );
      if (!ok) return;
    }
    onChange(detectableClusters);
    setDetectNotice(
      `Detected ${detectableClusters.length} cluster${detectableClusters.length === 1 ? '' : 's'} from ${knownUrls.length} URLs.`
    );
  };

  // Persist clusters to localStorage on every change. Domain-scoped key so
  // switching between domains doesn't bleed clusters.
  useEffect(() => {
    try {
      if (clusters.length === 0) {
        localStorage.removeItem(STORAGE_PREFIX + domain);
      } else {
        localStorage.setItem(STORAGE_PREFIX + domain, JSON.stringify(clusters));
      }
    } catch {
      // localStorage full or disabled — silent fallback, state still works
      // for the current session.
    }
  }, [clusters, domain]);

  const validation = useMemo(() => {
    const issues: { clusterIndex: number; message: string }[] = [];
    clusters.forEach((cluster, ci) => {
      const langs = new Set<string>();
      cluster.entries.forEach((entry, ei) => {
        if (!entry.lang && !entry.href) return; // blank rows are fine
        if (!isValidLangCode(entry.lang)) {
          issues.push({
            clusterIndex: ci,
            message: `Row ${ei + 1}: "${entry.lang}" is not a valid ISO 639-1 code (e.g. "en", "en-US", "x-default")`,
          });
        }
        if (!isValidUrl(entry.href)) {
          issues.push({ clusterIndex: ci, message: `Row ${ei + 1}: invalid URL` });
        }
        if (entry.lang && langs.has(entry.lang.toLowerCase())) {
          issues.push({
            clusterIndex: ci,
            message: `Duplicate language code "${entry.lang}" in cluster`,
          });
        }
        langs.add(entry.lang.toLowerCase());
      });
      if (cluster.entries.filter((e) => e.lang && e.href).length === 1) {
        issues.push({
          clusterIndex: ci,
          message: 'A cluster needs at least 2 language variants',
        });
      }
    });
    return issues;
  }, [clusters]);

  const summary = useMemo(() => {
    const clusterCount = clusters.length;
    const urlCount = new Set(
      clusters.flatMap((c) => c.entries.map((e) => e.href).filter(Boolean))
    ).size;
    return { clusterCount, urlCount };
  }, [clusters]);

  const addCluster = () => onChange([...clusters, emptyCluster()]);
  const removeCluster = (id: string) =>
    onChange(clusters.filter((c) => c.id !== id));
  const addEntry = (clusterId: string) =>
    onChange(
      clusters.map((c) =>
        c.id === clusterId ? { ...c, entries: [...c.entries, { lang: '', href: '' }] } : c
      )
    );
  const removeEntry = (clusterId: string, entryIndex: number) =>
    onChange(
      clusters.map((c) =>
        c.id === clusterId
          ? { ...c, entries: c.entries.filter((_, i) => i !== entryIndex) }
          : c
      )
    );
  const updateEntry = (
    clusterId: string,
    entryIndex: number,
    patch: Partial<HreflangEntry>
  ) =>
    onChange(
      clusters.map((c) =>
        c.id === clusterId
          ? {
              ...c,
              entries: c.entries.map((e, i) => (i === entryIndex ? { ...e, ...patch } : e)),
            }
          : c
      )
    );

  return (
    <div className="bg-white rounded-xl border">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <Languages className="w-5 h-5 text-indigo-600" />
          <div className="text-left">
            <h3 className="font-semibold">Hreflang clusters</h3>
            <p className="text-sm text-slate-500">
              {summary.clusterCount === 0
                ? 'Optional — declare language/region alternates for international SEO'
                : `${summary.clusterCount} cluster${summary.clusterCount === 1 ? '' : 's'} covering ${summary.urlCount} URL${summary.urlCount === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <ChevronDown className={cn('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="p-4 border-t space-y-4">
          <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Each cluster groups URLs that are translations of the same page. SitemapHost emits the
              cross-references as <code className="bg-white px-1 rounded">xhtml:link rel="alternate"</code> tags
              in the generated sitemap. Clusters are saved per-domain in your browser, so they
              persist across uploads.{' '}
              <a
                href="https://www.sitemaphost.app/glossary/hreflang-sitemap/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline inline-flex items-center gap-0.5"
              >
                Spec & examples
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {clusters.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              No hreflang clusters yet. Click "Add cluster" below to declare your first one.
            </div>
          )}

          {clusters.map((cluster, ci) => {
            const clusterIssues = validation.filter((v) => v.clusterIndex === ci);
            return (
              <div key={cluster.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">
                    Cluster {ci + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeCluster(cluster.id)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    title="Remove cluster"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {cluster.entries.map((entry, ei) => (
                    <div key={ei} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={entry.lang}
                        onChange={(e) =>
                          updateEntry(cluster.id, ei, { lang: e.target.value.trim() })
                        }
                        placeholder="en-US"
                        className="w-28 px-2 py-1.5 border rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <input
                        type="url"
                        value={entry.href}
                        onChange={(e) =>
                          updateEntry(cluster.id, ei, { href: e.target.value.trim() })
                        }
                        placeholder="https://example.com/en/page"
                        className="flex-1 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeEntry(cluster.id, ei)}
                        disabled={cluster.entries.length <= 2}
                        className={cn(
                          'p-1',
                          cluster.entries.length <= 2
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-400 hover:text-red-600'
                        )}
                        title={cluster.entries.length <= 2 ? 'Cluster needs at least 2 entries' : 'Remove row'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => addEntry(cluster.id)}
                  className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add language
                </button>
                {clusterIssues.length > 0 && (
                  <div className="space-y-1 pt-2 border-t">
                    {clusterIssues.map((issue, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-red-700">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{issue.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={addCluster}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" />
              Add cluster
            </button>
            {knownUrls.length > 0 && (
              <button
                type="button"
                onClick={handleAutoDetect}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  detectableClusters.length > 0
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'border border-slate-300 text-slate-500 hover:bg-slate-50'
                )}
                title="Scan uploaded URLs for /en/, /es-MX/, etc. and create clusters automatically"
              >
                <Wand2 className="w-4 h-4" />
                Auto-detect
                {detectableClusters.length > 0 && (
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {detectableClusters.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {detectNotice && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
              {detectNotice}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
